import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { BlogPost } from "../types/blogPost";
import { normalizeBlogMedia } from "../utils/blogMediaUtils";

interface UseBlogPostsOptions {
  /** If true, fetch only published posts. If false, fetch all (admin). Default: true */
  publishedOnly?: boolean;
  /** Max number of posts to fetch. Default: 5 for dashboard. */
  limit?: number;
}

export function useBlogPosts(options: UseBlogPostsOptions = {}) {
  const { publishedOnly = true, limit = 5 } = options;
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    let q = supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (publishedOnly) {
      q = q.eq("is_published", true);
    }

    const { data, error: err } = await q;

    if (err) {
      setError(err.message);
      setPosts([]);
    } else {
      setPosts(
        ((data as BlogPost[]) ?? []).map((p) => ({
          ...p,
          cover_image_path: p.cover_image_path ?? null,
          media: normalizeBlogMedia(p.media),
        }))
      );
    }
    setLoading(false);
  }, [publishedOnly, limit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { posts, loading, error, refetch: fetch };
}
